import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import moment from 'moment';
import { CreateUserRequest, CustomerProfile, MarketingChannel, MarketingContext, SubmitRetroCreditRequest, OrderConfirmationCreateAccountExperience } from '@/models';
import { AccountCreatorArgs } from '@/components/account/CmgCreateAccountForm/cmg-create-account-form';
import { LoyaltyExplainerModalState } from '@/components/modals/CmgLoyaltyExplainerModal/cmg-loyalty-explainer-modal';
import { EnrollmentOptions } from '@/services/loyalty/loyalty.service';
import { formatPhoneNumber, getStartOfYear, convertLocalToUTC, isLoyaltyEnabled, parseCountryCode, calculateAge, isSafariBrowser } from '@/helpers';
import { AUTOMATIC_PERSONALIZATION_AGE, countries } from '@/constants';
import { OrderAndHistoryMetricsRequest } from '@/models/sustainability';
import { CustomerErrorCode } from '@/services/customer/customer.service';
import { GuestInfoForm } from '@/store/checkout';
import { CountryCode } from '@/store/ui';
import { ButtonType } from '@/components/CmgButton/cmg-button';

@Component({
  name: 'CmgOrderConfirmationCreateAccount',
})
export default class CmgOrderConfirmationCreateAccount extends Vue {
  currentCountry = this.$appStore.state.ui.countryProfile.countryCode;
  showForm = true; // a flag to toggle animation when swapping country

  get guest(): GuestInfoForm {
    return this.$appStore.state.checkout.guest.info;
  }

  get isUKCustomer(): boolean {
    return this.currentCountry === CountryCode.GB;
  }

  get customer(): CustomerProfile | null {
    return this.$appStore.state.customer.profile;
  }

  get legalCopy(): any {
    return this.$services.config.getLegalText().createAnAccountOrderConfirmation;
  }

  get modalImageSrc(): string | undefined {
    return this.$services.config.getGuestOrderConfirmation()?.medallionImageUrl;
  }

  get prefilledRequest(): CreateUserRequest {
    const profile = this.$appStore.state.customer.profile;
    const lastOrder = this.$appStore.state.order.lastOrder.order && this.$appStore.state.order.lastOrder.order.order.delivery;

    return {
      firstName: (profile && profile.firstName) || '',
      emailAddress: (profile && profile.email) || '',
      phoneNumber: (lastOrder && lastOrder.dropOffContactPhoneNumber && formatPhoneNumber(lastOrder.dropOffContactPhoneNumber)) || (profile && profile.phoneNumber) || '',
    };
  }

  get signInContainerTitle(): string {
    if (this.currentCountry) {
      return countries[this.currentCountry.toLowerCase()].strings.signInContainerTitle;
    }

    return this.$appStore.state.ui.countryProfile.strings.signInContainerTitle;
  }

  get headerText(): string | undefined {
    if (this.isUKCustomer) {
      return 'CREATE AN ACCOUNT SO YOU CAN ORDER YOUR FAVOURITES EVEN FASTER.';
    }

    if (this.$services.config.getGuestOrderConfirmation()) {
      return this.$services.config.getGuestOrderConfirmation()?.header;
    }

    return this.$appStore.state.cms.orderConfirmationCreateAccountExperience?.header ?? this.$t('pages.orderConfirmation.createAccountSection.header') as string;
  }

  get subheaderText(): string | undefined {
    if (this.isUKCustomer) {
      return 'Just enter in a few more details to create an account.';
    }

    if (this.$services.config.getGuestOrderConfirmation()) {
      return this.$services.config.getGuestOrderConfirmation()?.description;
    }

    return this.$appStore.state.cms.orderConfirmationCreateAccountExperience?.description ?? this.$t('pages.orderConfirmation.createAccountSection.subHeader') as string;
  }

  get isLoyaltyEnabled(): boolean {
    return isLoyaltyEnabled(this.currentCountry);    
  }

  get isEnrolledInLoyalty(): boolean {
    return this.$services.loyalty.isEnrolledInLoyalty(this.$appStore.state.customer.profile);
  } 

  async mounted() {
    if(!this.$services.config.getGuestOrderConfirmation) {
      await this.$services.config.setGuestOrderConfirmation();
    }
  }

  async onCreateAccount(args: AccountCreatorArgs): Promise<void> {
    const request = args.request;

    const upgradeResponse = await this.$services.customer.upgradeGuestUser({
      firstName: request.firstName,
      lastName: request.lastName,
      emailAddress: request.emailAddress,
      password: request.password,
      phoneNumber: request.phoneNumber,
      country: args.country,
      dateOfBirth: request.dateOfBirth
    });

    // Pause account creation until after 2SV is complete
    if (upgradeResponse == CustomerErrorCode.TwoStepVerificationRequired || upgradeResponse == CustomerErrorCode.AccountVerificationRequired) {
      this.$services.ui.setModalVisibility('verifyMobileNumber', true, {
        email: request.emailAddress || '',
        phoneNumber: request.phoneNumber || '',
        oldPhoneNumber: "",
        requiresPasswordVerification: false,
        requiresSignOutOnFailure: true,
        onSuccess: async () => {
          const date = !isSafariBrowser() ? new Date(request.dateOfBirth!) : new Date(moment(request.dateOfBirth!, 'MM-DD-YYYY').format('YYYY-MM-DD'));
          const age = calculateAge(date);
          if (age < AUTOMATIC_PERSONALIZATION_AGE) {
            this.showPersonalizationOptionsModal(args);
          } else {
            if (args.country === CountryCode.US) {
              args.request.enrollInEmailMarketing = true;
            }
            if (args.country === CountryCode.US || args.country === CountryCode.CA) {
              args.enrollInPersonalization = true;
            }
            this.proceedAfterAccountCreation(args)
          }
        },
        onFailure: () => this.$services.ui.setHasFailed2SV(true),
      });
      Vue.nextTick(() => {
        this.$services.ui.focusDiv('.cmg-verify-mobile-number-modal');
      });
    } else if (upgradeResponse == CustomerErrorCode.UserAgeIneligible) {      
      this.$services.ui.setModalVisibility('basicError', true, {
        header: 'AW SHUCKS',
        content: 'We aren\'t able to create your account at this time<p>Check out our <a href="/privacy-policy">Privacy Policy</a>, <a href="/rewards-terms">Rewards Terms</a>, and <a href="/terms-of-use">Terms of use</a> to learn more.' ,
        options: [
          {
            text: 'Return to Home',
            type: ButtonType.Primary,
            onClick: () => {                  
              this.$services.ui.setModalVisibility('basicError', false);
              this.$services.ui.setHasFailed2SV(true);
            },
          },
        ],
        onClose: () => { this.$services.ui.setHasFailed2SV(true) }
      });
    }
    else {
      this.proceedAfterAccountCreation(args);
    }    
  }

  async showPersonalizationOptionsModal(args: AccountCreatorArgs): Promise<void> {
     this.$services.ui.setModalVisibility('personalizedContent', true, {
      showEmailMarketingOptions: args.country === CountryCode.US,
      onSuccess: async () => {
        if (args.country === CountryCode.US) {
          args.request.enrollInEmailMarketing = this.$appStore.state.customer.optedIntoEmailMarketingFromOptions;
        }
        if (args.country === CountryCode.US || args.country === CountryCode.UK) {
          args.enrollInPersonalization = this.$appStore.state.customer.optedIntoPersonalizationMessaging;
        }
        this.proceedAfterAccountCreation(args);
      },
      onClose: async (link: string) => { 
        await this.proceedAfterAccountCreation(args);
        if (link.length > 0) {
          window.location.href = link;
        }
      }
    });
  }

  async proceedAfterAccountCreation(args: AccountCreatorArgs): Promise<void> {
    const request = args.request;
    const order = this.$appStore.state.order.lastOrder.order;

    await this.$services.customer.updateCustomer({
      firstName: request.firstName,
      lastName: request.lastName,
      email: request.emailAddress,
      phoneNumber: request.phoneNumber,
      country: args.country,
      isGuest: false,
      dateOfBirth: args.dateOfBirth,
      optedIntoPersonalization: args.enrollInPersonalization
    });

    if (!order) {
      return this.$services.logger.unreachable('Attempted to upgrade account from order confirmation page without an order');
    }
    const orderId = order.order.orderId;
    const meals = order.order.meals || [];
    const orderSubmitDateTimeUtc = convertLocalToUTC(order.order.orderDateTimeLocal) || moment().utc().format(); // prettier-ignore

    if (args.request.enrollInLoyalty) {
      const enrollmentOptions: EnrollmentOptions = {
         customer: this.customer, 
         pageTitle: document.title,
         shouldSubscribeToMarketingEmail: args.request.enrollInEmailMarketing,
         shouldSubscribeToMarketingSMS: args.request.enrollInSmsMarketing
      };
      await this.$services.loyalty.enrollInLoyalty(enrollmentOptions);
      this.$services.ui.showOnboardingModal(this.$services.customer, this.customer ?? undefined);      
      this.$services.customer.setJoinedLoyaltyOnOrderConfirmationPage(true);

      const retroCreditRequest: SubmitRetroCreditRequest = {
        restaurantId: order.order.restaurantId,
        checkNumber: this.$services.utils.generateRandomNumberString(5), // Need to send dummy 5 digit check number - ok because onlineOrderId is populated
        transactionDate: order.order.orderDateTimeLocal || moment().format(),
        transactionAmount: order.order.orderTotalAmount.toString(),
        onlineOrderId: order.order.orderId
      };
      this.$services.retroCredit.submit(retroCreditRequest);

      this.$services.ui.setLastSelectedElement('points-on-the-way');
    } else {
      const subscribeTo = this.$services.customer.subscribeToMarketingChannel.bind(this.$services.customer);
      const results = await Promise.allSettled([
        ...(args.request.enrollInSmsMarketing ? [subscribeTo(MarketingChannel.Sms, MarketingContext.Marketing)] : []),
        ...(args.request.enrollInEmailMarketing ? [subscribeTo(MarketingChannel.Email, MarketingContext.Marketing)] : []),
      ]);
      results.forEach(result => {
        if (result.status === 'rejected') {
          this.$services.logger.error('Unable to subscribe to marketing channel', undefined, result.reason);
        }
      });
    }

    // Keep sustainability metrics
    if (this.currentCountry !== CountryCode.GB) {
      const postSustainabilityMetricsBody: OrderAndHistoryMetricsRequest = {
        orderId,
        isGuest: false,
        country: args.country,
        meals,
        startDate: getStartOfYear(),
        orderSubmitDateTimeUtc,
      };
      await this.$services.sustainability.postCurrentOrderAndHistorySustainabilityMetrics(postSustainabilityMetricsBody);
    }

    this.$services.ui.firstFocus();
      
    await this.$services.customer.refreshCustomerProfileInfo();
  }

  onClickSignIn(event: any): void {
    if (event.target.className) {
      this.$services.ui.setLastSelectedElement(event.target.className);
    }

    this.$services.ui.setModalVisibility('signIn', true, {
      onClose: () => this.proceedAfterSignIn()
    });
    this.$services.ui.firstFocus();
  }

  async proceedAfterSignIn() {
    await this.$services.customer.refreshCustomerProfileInfo();

    if(this.isEnrolledInLoyalty) {
      const order = this.$store.state.order.lastOrder.order.order;
      
      const retroCreditRequest: SubmitRetroCreditRequest = {
        restaurantId: order.restaurantId,
        checkNumber: this.$services.utils.generateRandomNumberString(5), // Need to send dummy 5 digit check number - ok because onlineOrderId is populated
        transactionDate: order.orderDateTimeLocal || moment().format(),
        transactionAmount: order.orderTotalAmount,
        onlineOrderId: order.orderId
      };      
      
      await this.$services.retroCredit.submit(retroCreditRequest);
    }  
  }

  onClickExplainRewards(event: any): void {
    if (event.target.className) {
      this.$services.ui.setLastSelectedElement(event.target.className);
    }
    this.$services.ui.setModalVisibility('loyaltyExplainer', true, {
      state: LoyaltyExplainerModalState.Explainer,
    });
    this.$services.ui.firstFocus();
  }

  setCurrentCountry(country: string): void {
    this.currentCountry = country;

    this.showForm = false;
    this.$nextTick(() => {
      this.showForm = true;
    });
  }
}
